import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { X, Facebook, Twitter, Linkedin, Instagram, Youtube, Globe } from 'lucide-react';
import type { CustomerSocialProfile } from '../../types/customer360';

interface SocialProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (profileData: Partial<CustomerSocialProfile>) => void;
  profile?: CustomerSocialProfile;
  customerId: number;
}

const platformOptions: Array<{
  value: CustomerSocialProfile['platform'];
  label: string;
  icon: typeof Facebook;
  baseUrl: string;
  placeholder: string;
}> = [
  {
    value: 'facebook',
    label: 'Facebook',
    icon: Facebook,
    baseUrl: 'https://facebook.com/',
    placeholder: 'username or full URL'
  },
  {
    value: 'twitter',
    label: 'Twitter/X',
    icon: Twitter,
    baseUrl: 'https://twitter.com/',
    placeholder: '@username or full URL'
  },
  {
    value: 'linkedin',
    label: 'LinkedIn',
    icon: Linkedin,
    baseUrl: 'https://linkedin.com/in/',
    placeholder: 'username or full URL'
  },
  {
    value: 'instagram',
    label: 'Instagram',
    icon: Instagram,
    baseUrl: 'https://instagram.com/',
    placeholder: '@username or full URL'
  },
  {
    value: 'youtube',
    label: 'YouTube',
    icon: Youtube,
    baseUrl: 'https://youtube.com/',
    placeholder: 'channel URL'
  },
  {
    value: 'tiktok',
    label: 'TikTok',
    icon: Globe,
    baseUrl: 'https://tiktok.com/@',
    placeholder: '@username or full URL'
  },
  {
    value: 'other',
    label: 'Other',
    icon: Globe,
    baseUrl: '',
    placeholder: 'Full URL'
  }
];

export default function SocialProfileModal({
  isOpen,
  onClose,
  onSave,
  profile,
  customerId
}: SocialProfileModalProps) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  
  const [formData, setFormData] = useState<Partial<CustomerSocialProfile>>({
    platform: 'linkedin',
    profile_url: '',
    profile_handle: ''
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (profile) {
      setFormData({
        platform: profile.platform,
        profile_url: profile.profile_url || '',
        profile_handle: profile.profile_handle || ''
      });
    } else {
      setFormData({
        platform: 'linkedin',
        profile_url: '',
        profile_handle: ''
      });
    }
    setErrors({});
  }, [profile, isOpen]);

  const selectedPlatform = platformOptions.find(p => p.value === formData.platform) || platformOptions[0];

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.profile_handle && !formData.profile_url) {
      newErrors.general = 'Please provide either a username/handle or profile URL';
    }

    // URL validation if provided
    if (formData.profile_url) {
      try {
        new URL(formData.profile_url);
      } catch {
        // Check if it's a partial URL (e.g., "username" instead of full URL)
        if (!formData.profile_url.startsWith('http')) {
          // Auto-complete with base URL
          const completeUrl = selectedPlatform.baseUrl + formData.profile_url.replace(/^[@/]+/, '');
          setFormData(prev => ({ ...prev, profile_url: completeUrl }));
        } else {
          newErrors.profile_url = 'Please enter a valid URL';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      // Normalize the URL if handle is provided but URL is not
      let finalData = { ...formData };
      
      if (formData.profile_handle && !formData.profile_url) {
        const handle = formData.profile_handle.replace(/^[@/]+/, '');
        finalData.profile_url = selectedPlatform.baseUrl + handle;
      }

      onSave(finalData);
    }
  };

  const handlePlatformChange = (platform: CustomerSocialProfile['platform']) => {
    setFormData(prev => ({
      ...prev,
      platform,
      profile_url: '',
      profile_handle: ''
    }));
    setErrors({});
  };

  const handleProfileHandleChange = (value: string) => {
    setFormData(prev => ({ ...prev, profile_handle: value }));
    
    // Auto-generate URL from handle if platform has baseUrl
    if (selectedPlatform.baseUrl && value) {
      const handle = value.replace(/^[@/]+/, '');
      setFormData(prev => ({
        ...prev,
        profile_url: selectedPlatform.baseUrl + handle
      }));
    }
    
    setErrors({});
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black/50 transition-opacity"
          onClick={onClose}
        />

        {/* Modal */}
        <div className={`relative w-full max-w-lg rounded-lg shadow-xl ${
          isDark ? 'bg-gray-800' : 'bg-white'
        }`}>
        {/* Header */}
        <div
          className={`flex items-center justify-between p-6 border-b ${
            isDark ? 'border-gray-700' : 'border-gray-200'
          }`}
        >
          <h2 className={`text-xl font-semibold ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
            {profile ? 'Edit Social Profile' : 'Add Social Profile'}
          </h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
            }`}
          >
            <X className={`h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Platform Selection */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              Platform <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-3">
              {platformOptions.map((platform) => {
                const Icon = platform.icon;
                const isSelected = formData.platform === platform.value;
                return (
                  <button
                    key={platform.value}
                    type="button"
                    onClick={() => handlePlatformChange(platform.value)}
                    className={`flex items-center gap-3 p-3 rounded-lg border-2 transition-all ${
                      isSelected
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : isDark
                        ? 'border-gray-700 hover:border-gray-600 bg-gray-700/50'
                        : 'border-gray-200 hover:border-gray-300 bg-white'
                    }`}
                  >
                    <Icon
                      className={`h-5 w-5 ${
                        isSelected ? 'text-blue-500' : isDark ? 'text-gray-400' : 'text-gray-600'
                      }`}
                    />
                    <span
                      className={`text-sm font-medium ${
                        isSelected ? 'text-blue-500' : isDark ? 'text-gray-100' : 'text-gray-900'
                      }`}
                    >
                      {platform.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Profile Handle */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              Username/Handle
            </label>
            <div className="relative">
              {selectedPlatform.baseUrl && (
                <span
                  className={`absolute left-3 top-1/2 -translate-y-1/2 text-sm ${
                    isDark ? 'text-gray-400' : 'text-gray-600'
                  }`}
                >
                  @
                </span>
              )}
              <input
                type="text"
                value={formData.profile_handle || ''}
                onChange={(e) => handleProfileHandleChange(e.target.value)}
                placeholder={selectedPlatform.placeholder}
                className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  selectedPlatform.baseUrl ? 'pl-8' : ''
                } ${
                  isDark
                    ? 'bg-gray-700 border-gray-600 text-gray-100'
                    : 'bg-white border-gray-300 text-gray-900'
                } ${errors.profile_handle ? 'border-red-500' : ''}`}
              />
            </div>
            {selectedPlatform.baseUrl && (
              <p className={`mt-1 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                We'll automatically create the full URL: {selectedPlatform.baseUrl}username
              </p>
            )}
          </div>

          {/* Profile URL */}
          <div>
            <label className={`block text-sm font-medium mb-2 ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
              Profile URL
            </label>
            <input
              type="url"
              value={formData.profile_url || ''}
              onChange={(e) => {
                setFormData(prev => ({ ...prev, profile_url: e.target.value }));
                setErrors({});
              }}
              placeholder="https://..."
              className={`w-full px-3 py-2 rounded-lg border focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDark
                  ? 'bg-gray-700 border-gray-600 text-gray-100'
                  : 'bg-white border-gray-300 text-gray-900'
              } ${errors.profile_url ? 'border-red-500' : ''}`}
            />
            {errors.profile_url && (
              <p className="mt-1 text-xs text-red-500">{errors.profile_url}</p>
            )}
          </div>

          {/* General Error */}
          {errors.general && (
            <div
              className={`p-3 rounded-lg border-l-4 border-red-500 ${
                isDark ? 'bg-red-900/20' : 'bg-red-50'
              }`}
            >
              <p className={`text-sm ${isDark ? 'text-red-300' : 'text-red-600'}`}>
                {errors.general}
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className={`flex-1 px-4 py-2 rounded-lg border transition-colors ${
                isDark
                  ? 'border-gray-600 text-gray-100 bg-gray-800 hover:bg-gray-700'
                  : 'border-gray-300 text-gray-900 bg-white hover:bg-gray-50'
              }`}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
            >
              {profile ? 'Update Profile' : 'Add Profile'}
            </button>
          </div>
        </form>
      </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}
